import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { attachScrollTriggerOrPlayNow } from "../utils/scrollTriggerHelpers";

gsap.registerPlugin(ScrollTrigger);

/**
 * Counter block animation.
 *
 * Orchestrazione:
 *  Per ogni counter (in sequenza):
 *   1. Numero: fade + blur + slide-up (0.6s, power2.out)
 *   2. Count-up del numero da 0 al target (1.5s, power2.out)
 *   3. Testo: fade + blur (0.5s) — parte 0.4s dopo il numero
 *   4. Divider successivo (se non last): fade (0.4s)
 *  Step tra counter: 1.7s
 *
 * Trigger: ScrollTrigger una sola volta quando il blocco entra in viewport
 * (start "top 85%"), coerente con scrollAnimations.js.
 *
 * Performance: timeline unica per blocco, niente loop infiniti.
 * GSAP gestisce will-change e cleanup automatici.
 */

const COUNT_UP_DURATION = 1.5;
const STEP_BETWEEN_COUNTERS = 0.9;
const NUMBER_REVEAL_DURATION = 1.0;
const LABEL_REVEAL_DURATION = 0.8;
const DIVIDER_REVEAL_DURATION = 0.4;
const LABEL_DELAY_AFTER_NUMBER = 0.4;
const NUMBER_INITIAL_BLUR = 12;
const LABEL_INITIAL_BLUR = 8;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const initCounterBlock = (block) => {
  const items = block.querySelectorAll(".counter-grid__item");
  const numbers = block.querySelectorAll(".counter-grid__number");
  const labels = block.querySelectorAll(".counter-grid__label");
  const dividers = block.querySelectorAll(".counter-grid__divider");

  if (!items.length) return;

  // Stato iniziale: tutti i numeri a 0 (markup mostrava il target)
  numbers.forEach((el) => {
    const target = parseInt(el.getAttribute("data-counter-target"), 10) || 0;
    el.setAttribute("data-counter-target", target); // assicura sia numero pulito
    el.textContent = "0";
  });

  // Reduced motion: niente animazione, mostro subito tutto
  if (prefersReducedMotion()) {
    numbers.forEach((el) => {
      el.textContent = el.getAttribute("data-counter-target");
    });
    block.setAttribute("data-counter-ready", "true");
    return;
  }

  // Stato iniziale GSAP per numero + label + divider
  gsap.set(numbers, { opacity: 0, y: 16, filter: `blur(${NUMBER_INITIAL_BLUR}px)` });
  gsap.set(labels, { opacity: 0, filter: `blur(${LABEL_INITIAL_BLUR}px)` });
  gsap.set(dividers, { opacity: 0 });

  // Timeline master orchestratore (paused, attiviamo via helper)
  const tl = gsap.timeline({ paused: true });

  items.forEach((item, idx) => {
    const stepStart = idx * STEP_BETWEEN_COUNTERS;
    const number = item.querySelector(".counter-grid__number");
    const label = item.querySelector(".counter-grid__label");
    const target = parseInt(number.getAttribute("data-counter-target"), 10) || 0;

    // 1. Numero appare (fade + blur + slide-up)
    tl.to(number, {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      duration: NUMBER_REVEAL_DURATION,
      ease: "power2.out",
    }, stepStart);

    // 2. Count-up del numero (parte poco dopo l'apparizione)
    tl.to(number, {
      textContent: target,
      duration: COUNT_UP_DURATION,
      ease: "power2.out",
      snap: { textContent: 1 },
    }, stepStart + 0.2);

    // 3. Label appare (fade + blur)
    tl.to(label, {
      opacity: 1,
      filter: "blur(0px)",
      duration: LABEL_REVEAL_DURATION,
      ease: "power2.out",
    }, stepStart + LABEL_DELAY_AFTER_NUMBER);

    // 4. Divider successivo (se non è l'ultimo counter)
    // Appare insieme al counter successivo per coerenza visiva
    if (idx < dividers.length) {
      const divider = dividers[idx];
      if (divider) {
        tl.to(divider, {
          opacity: 1,
          duration: DIVIDER_REVEAL_DURATION,
          ease: "power2.out",
        }, stepStart + STEP_BETWEEN_COUNTERS);
      }
    }
  });

  // Attach ScrollTrigger o play immediato se già in viewport
  attachScrollTriggerOrPlayNow(tl, {
    trigger: block,
    onComplete: () => block.setAttribute("data-counter-ready", "true"),
  });
};

const initAllCounters = () => {
  const blocks = document.querySelectorAll(".blocco_counter[data-counter-ready]");
  blocks.forEach(initCounterBlock);
};

// Named export: usabile da altri blocchi che contengono internamente una
// `.counter-grid` (es. blocco percorso). Il chiamante passa il container che
// avvolge `.counter-grid__item`, `.counter-grid__number`, ecc.
export { initCounterBlock };

export default () => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllCounters);
  } else {
    initAllCounters();
  }
};